import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import {
    summerPromo2026,
    summerPromo2026Anonym,
    summerPromo2026ShowInterval,
} from 'reactApp/appHelpers/featuresHelpers/features/summerPromo2026';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { getUserIsLoaded, UserSelectors } from 'reactApp/modules/user/user.selectors';
import { isCloudOrMailOrApiOverQuota } from 'reactApp/modules/userQuota/overquota.helpers';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';

export const PROMO_ID = 'summer-campaign-2026-modal';
export const SUMMER_PROMO_2026_MODAL_QUERY_PARAM = 'summer-promo-2026-modal';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/SummerCampaign2026Modal/SummerCampaign2026Modal'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const summerCampaign2026ModalCreator: ModelCreator = ({ models, render, stores }) => {
    return new PopupModel(
        PROMO_ID,
        models.base,
        new BaseModelProps(PROMO_ID, {
            store: new LocalStorageStore(PROMO_ID, stores.localStorage),
            renderer: new ReactRenderer(PROMO_ID, SuspendedComponent, render.react),
            closable: true,
            showInterval: summerPromo2026ShowInterval / 1000,
            showLimit: 1000,
            closeLimit: 1000,
            templateData: {},
            ignoreTestOnRedraw: true,
            markAsCompleted: true,
            hideOnCompleted: true,
            hideOnSnooze: true,
            test: (resolve, reject) => {
                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);
                const searchParams = new URL(window.location.href).searchParams;

                if (storage !== EStorageType.quotaCleaner) {
                    reject();
                    return;
                }

                if (searchParams.get(SUMMER_PROMO_2026_MODAL_QUERY_PARAM)) {
                    reject();
                    return;
                }

                const action = searchParams.get('action') ?? '';
                if (action) {
                    reject();
                    sendXray(['promo-test', PROMO_ID, 'rejectbyaction']);
                    return;
                }

                const isUserLoaded = getUserIsLoaded(state);
                if (!isUserLoaded) {
                    reject();
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isCorpUser = UserSelectors.isCorpUser(state);
                const isPaidUser = UserSelectors.isPaidUser(state);
                const isOverquota = isCloudOrMailOrApiOverQuota(state);
                const isAnonymous = UserSelectors.isAnonymous(state);

                const isFeatureEnabled = (isAnonymous && summerPromo2026Anonym) || (!isAnonymous && summerPromo2026);

                const isReject = !isFeatureEnabled || isBizUser || isPaidUser || isOverquota || isCorpUser;

                sendXray(['promo-test', PROMO_ID, isReject ? 'reject' : 'resolve']);

                if (isReject) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
};
